import React, { useEffect, useState } from "react";
import { api, COUPON_STATUS_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search } from "lucide-react";

const STATUS_COLORS = {
  AKTIV: "bg-[#EAF0EC] text-[#3B5843] border-[#4A6B53]/30",
  FELHASZNALVA: "bg-[#F3EDE2] text-[#8B4A2E] border-[#C29B78]/50",
  LEJART: "bg-[#2C221E]/10 text-[#5C524A] border-[#2C221E]/20",
  ERVENYTELEN: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminCoupons() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({ coupons: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/coupons", { params: { q, status } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [q, status, bump]);

  const updateStatus = async (code, newStatus) => {
    try {
      await api.patch(`/admin/coupons/${code}`, { status: newStatus });
      toast.success(`Kupon státusz: ${COUPON_STATUS_LABELS[newStatus]}`);
      setBump((b) => b + 1);
    } catch {
      toast.error("Nem sikerült frissíteni");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="section-label">Kuponok</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#201B18] mt-1">Kezelés · {data.count}</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8075]" />
          <Input placeholder="Keresés kódra, emailre..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-coupons-search" className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-coupons-filter-status" className="w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Minden státusz</SelectItem>
            {Object.entries(COUPON_STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl bg-white border border-[#2C221E]/10 overflow-hidden" data-testid="admin-coupons-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kód</TableHead>
                <TableHead>Felhasználó</TableHead>
                <TableHead>Kedvezmény</TableHead>
                <TableHead>Státusz</TableHead>
                <TableHead>Létrehozva</TableHead>
                <TableHead>Felhasználva</TableHead>
                <TableHead className="text-right">Művelet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-[#8C8075] py-10">Betöltés...</TableCell></TableRow>
              ) : data.coupons.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-[#8C8075] py-10">Még nincs adat.</TableCell></TableRow>
              ) : (
                data.coupons.map((c) => (
                  <TableRow key={c.code}>
                    <TableCell className="font-mono-brand text-xs font-bold">{c.code}</TableCell>
                    <TableCell className="text-[#5C524A]">{c.subscriber_email}</TableCell>
                    <TableCell>{c.discount_percent}%</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[c.status] + " font-mono-brand text-[10px]"}>{COUPON_STATUS_LABELS[c.status]}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-[#8C8075]">{new Date(c.created_at).toLocaleDateString("hu-HU")}</TableCell>
                    <TableCell className="text-xs text-[#8C8075]">{c.used_at ? new Date(c.used_at).toLocaleDateString("hu-HU") : "–"}</TableCell>
                    <TableCell className="text-right">
                      <Select value={c.status} onValueChange={(v) => updateStatus(c.code, v)}>
                        <SelectTrigger data-testid={`admin-coupon-status-${c.code}`} className="w-36 h-8 text-xs ml-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(COUPON_STATUS_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
